import {GrmMediaPipeObject, GrmMediaPipeInterface} from "@/lib/gooroomee/mediapipe/grm-mediapipe/GrmMediaPipeInterface";

/**
 * 레거시 브라우저용 Face Detector (OffscreenCanvas 미지원 환경)
 * iOS Safari 16.3 이하, Chrome 68 이하 등에서 사용
 */
export default class GrmFaceDetectorLegacy extends GrmMediaPipeInterface {
    constructor(options = {}) {
        super(options);
        this.detectionMethod = null;
        this.isInitialized = false;
        this.faceDetection = null; // MediaPipe FaceDetection 인스턴스
        this.lastDetectionResults = []; // MediaPipe 감지 결과 저장
        
        // Confidence tracking (GrmFaceDetector와 동일)
        this.confidence = {
            detected: 1, // 최초는 pass
            lastDetectedTime: 0,
            cooldownTime: 500,
            reset () {
                Object.assign(this, {
                    detected: 0,
                    lastDetectedTime: 0,
                });
            },
            isAreaDetected (canvas, box, ratio) {
                const {width, height} = canvas;
                const widthPadding = (width - (width * ratio)) / 2;
                const heightPadding = (height - (height * ratio)) / 2;
                const inX = box.x > widthPadding && box.x+box.width < width - widthPadding;
                const inY = box.y > heightPadding && box.y+box.height < height - heightPadding;
                return inX && inY;
            }
        };
        
        // Face smoothing (기존 로직 유지)
        this.faceSmoothing = {
            prevBoxArr: [],
            easing: {
                coordi: 0.2,
                size: 0.1
            },
            reset: function(){
                if(this.prevBoxArr.length > 0){
                    this.prevBoxArr = [];
                }
            },
            lerp: function(start, end, amt) {
                return start + (end - start) * amt;
            },
            smoothBox: function(box, idx) {
                idx = idx || 0;
                let {prevBoxArr, lerp, easing} = this;
                let prevBox = prevBoxArr[idx];
                if (!prevBox) {
                    let { x, y, width, height } = box;
                    prevBoxArr[idx] = { x, y, width, height };
                    return box;
                }
                let result = prevBoxArr[idx] = {
                    x: lerp(prevBox.x, box.x, easing.coordi),
                    y: lerp(prevBox.y, box.y, easing.coordi),
                    width: lerp(prevBox.width, box.width, easing.size),
                    height: lerp(prevBox.height, box.height, easing.size),
                };
                return result;
            }
        };
    }

    async init() {
        console.log('GrmFaceDetectorLegacy: Initializing for OffscreenCanvas-unsupported browsers');
        
        // MediaPipe Face Detection 초기화 시도
        try {
            await this.initMediaPipeFaceDetection();
            this.detectionMethod = 'mediapipe';
            console.log('✅ GrmFaceDetectorLegacy: Using MediaPipe Face Detection');
        } catch (error) {
            console.warn('⚠️ MediaPipe initialization failed, using Simple Detection:', error);
            this.detectionMethod = 'simple';
        }
        
        this.isInitialized = true;
        await this.setOptions(this.options);
    }

    // MediaPipe Face Detection 초기화
    async initMediaPipeFaceDetection() {
        if (this.faceDetection) {
            return; // 이미 초기화됨
        }
        
        // 동적 import로 MediaPipe 로드
        const { FaceDetection } = await import('@mediapipe/face_detection');
        
        this.faceDetection = new FaceDetection({
            locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4/${file}`
        });
        
        this.faceDetection.setOptions({
            model: 'short',
            minDetectionConfidence: 0.5,
        });
        
        // 결과 핸들러
        this.faceDetection.onResults((results) => {
            this.lastDetectionResults = results.detections || [];
        });
        
        // WASM 초기화 완료 대기
        await this.faceDetection.initialize();
    }


    async setOptions(options) {
        // faceImage 처리 (DOM Canvas 사용)
        if (options.faceImage) {
            try {
                // DOM Canvas를 사용하여 ImageBitmap 생성
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = options.faceImage;
                });
                
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                
                options.faceImage = canvas; // Canvas 객체로 저장
            } catch (e) {
                console.error('faceImage processing failed:', e);
            }
        }
        
        super.setOptions(options);
    }

    // trackApp 인터페이스 호환성을 위한 메서드들
    async setFaceImage(faceImage) {
        console.log('GrmFaceDetectorLegacy: setFaceImage called');
        
        if (!this.options) return;
        
        // faceImage가 URL 문자열인 경우 Canvas로 변환
        if (typeof faceImage === 'string') {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = faceImage;
                });
                
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                
                this.options.faceImage = canvas;
                console.log('✅ faceImage converted to Canvas:', canvas.width, 'x', canvas.height);
            } catch (error) {
                console.error('❌ Failed to convert faceImage:', error);
                this.options.faceImage = null;
            }
        } else {
            // 이미 Canvas 객체인 경우
            this.options.faceImage = faceImage;
        }
    }

    setFaceDrawType(drawType) {
        console.log('GrmFaceDetectorLegacy: setFaceDrawType called with:', drawType);
        if (this.options) {
            this.options.drawType = drawType;
        }
    }

    async render({image, canvas, ctx}) {
        if (!this.isInitialized) {
            console.warn('GrmFaceDetectorLegacy not initialized');
            return;
        }
        
        const {drawType} = this.options;
        if (!drawType) return;

        // 이미지 속성 설정 (기존 로직 유지)
        [image, canvas].forEach(obj => {
            if (!obj) return;
            Object.assign(obj, {
                complete: true,
                naturalWidth: obj.width,
                naturalHeight: obj.height,
                videoWidth: obj.width,
                videoHeight: obj.height,
            });
        });

        // Face Detection 실행
        const detections = await this.detectFaces(image);
        
        if (detections.length === 0) {
            this.confidence.reset();
            this.faceSmoothing.reset();
            return;
        }

        // 렌더링 (Confidence 로직 포함)
        const now = Date.now();
        let numberOfFace = this.options.numberOfFace || 1;
        let limitScore = this.options.limitScore || 0.1;
        
        detections.slice(0, numberOfFace).forEach((detection, idx, arr) => {
            if (detection.score < limitScore) {
                return;
            }

            // Confidence 체크 (낮은 신뢰도 필터링)
            if (detection.score <= 0.1) {
                const {confidence} = this;
                if (confidence.isAreaDetected(ctx.canvas, detection.box, 0.8)) {
                    return;
                }

                if (confidence.detected === 0) {
                    confidence.detected = 1;
                    confidence.lastDetectedTime = now;
                    return;
                }

                let time = now - confidence.lastDetectedTime;
                if (time > confidence.cooldownTime) {
                    if (confidence.isAreaDetected(ctx.canvas, detection.box, 0.8)) {
                        confidence.detected = 0;
                        return;
                    }
                    confidence.detected = 1;
                } else {
                    confidence.detected++;
                }

                if (detection.score < 0.3 && time < 330) {
                    return;
                } else if (detection.score < 0.4 && time < 160) {
                    return;
                } else if (detection.score < 0.5 && time < 70) {
                    return;
                }
            } else {
                this.confidence.detected++;
            }

            switch (drawType) {
                case 'FaceImage':
                case 'faceImage': {
                    this.renderFaceImage({canvas, ctx, detection, idx});
                    break;
                }
                case 'box': {
                    if (idx === 0) {
                        ctx.save();
                        ctx.strokeStyle = 'red';
                        ctx.lineWidth = 2;
                    }
                    let { x, y, width, height } = this.faceSmoothing.smoothBox(detection.box, idx);
                    ctx.strokeRect(x, y, width, height);
                    if (idx === arr.length - 1) {
                        ctx.restore();
                    }
                    break;
                }
                case 'mosaic': {
                    this.renderFaceMosaicLegacy({canvas, ctx, detection, idx});
                    break;
                }
            }
        });
    }

    // Face Detection 실행 (방식별 분기)
    async detectFaces(image) {
        switch (this.detectionMethod) {
            case 'mediapipe':
                return await this.detectWithMediaPipe(image);
            case 'simple':
                return [this.detectSimple(image)];
            default:
                return [];
        }
    }

    // MediaPipe로 Face Detection
    async detectWithMediaPipe(image) {
        if (!this.faceDetection) return [];
        
        try {
            // MediaPipe에 이미지 전달
            await this.faceDetection.send({ image });
            
            // 결과 대기 (MediaPipe는 비동기로 onResults 콜백 호출)
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // MediaPipe 결과를 공통 포맷으로 변환
            return this.lastDetectionResults.map(detection => {
                const box = detection.boundingBox;
                return {
                    box: {
                        x: box.xCenter * image.width - (box.width * image.width) / 2,
                        y: box.yCenter * image.height - (box.height * image.height) / 2,
                        width: box.width * image.width,
                        height: box.height * image.height,
                    },
                    score: detection.score || 0.5
                };
            });
        } catch (error) {
            console.warn('MediaPipe detection failed:', error);
            return [];
        }
    }


    // Simple Face Detection (100% 호환)
    detectSimple(image) {
        const centerX = image.width * 0.5;
        const centerY = image.height * 0.4;
        const faceWidth = image.width * 0.3;
        const faceHeight = image.height * 0.4;
        
        return {
            box: {
                x: centerX - faceWidth / 2,
                y: centerY - faceHeight / 2,
                width: faceWidth,
                height: faceHeight,
            },
            score: 0.8
        };
    }

    // Face Image 렌더링 (기존 로직 유지)
    renderFaceImage({ctx, detection, idx}) {
        if (!detection) return;
        const {faceImage} = this.options;
        if (!faceImage) return;

        const {faceSmoothing} = this;
        let { x, y, width, height } = faceSmoothing.smoothBox(detection.box, idx);

        let p = 1.5;
        let ratio = Math.min(
            width / faceImage.width,
            height / faceImage.height
        ) * p;

        let faceWidth = faceImage.width * ratio;
        let faceHeight = faceImage.height * ratio;
        x -= (faceWidth - width) / 2;
        y -= (faceHeight - height) / 2;

        ctx.drawImage(
            faceImage,
            0, 0,
            faceImage.width, faceImage.height,
            x, y,
            faceWidth, faceHeight
        );
    }

    // 모자이크 렌더링 (DOM Canvas 사용)
    renderFaceMosaicLegacy({ctx, detection, idx}) {
        let { x, y, width, height } = this.faceSmoothing.smoothBox(detection.box, idx);
        
        // 크기 조정
        let ratio = 1.5;
        let w = width * 1.5;
        let h = height * 1.5;
        x -= (w - width) / 2;
        y -= (h - height) / 2;
        width = w;
        height = h;

        // DOM Canvas 사용 (OffscreenCanvas 대신)
        const mCvs = this._m_cvs = this._m_cvs || (() => {
            const canvas = document.createElement('canvas');
            canvas.width = ctx.canvas.width;
            canvas.height = ctx.canvas.height;
            return canvas;
        })();
        
        const mCtx = this._m_cvs_ctx = this._m_cvs_ctx || (() => {
            const context = mCvs.getContext('2d');
            context.filter = 'blur(20px)';
            return context;
        })();
        
        // 크기 조정
        if (mCvs.width < width) {
            mCvs.width = width;
        }
        if (mCvs.height < height) {
            mCvs.height = height;
        }
        
        // 모자이크 처리
        mCtx.drawImage(ctx.canvas,
            x, y, width, height,
            0, 0, width, height,
        );
        ctx.drawImage(mCvs,
            0, 0, width, height,
            x, y, width, height
        );
    }
}

// 레거시 Face Detector 등록
GrmMediaPipeObject.add('FaceDetectorLegacy', GrmFaceDetectorLegacy);
